// @catalog-federation — value formats, canonical bytes, strict reading
// ============================================================================
// The TypeScript twin of libs/federation/FederationJson.cs (docs/cid-governance.md
// M0b). Every federation payload shares these value formats, so a payload built
// here and one parsed back are byte-identical after JCS:
//
//   • timestamps are RFC 3339 UTC to the second, `yyyy-MM-ddTHH:mm:ssZ`;
//   • byte strings are standard base64 with padding;
//   • uuids are lowercase and hyphenated;
//   • integers stay within ±2^53 (the C# side caps them there for us);
//   • signing profiles are `{ algorithm, parameter_set }`.
//
// A reader accepts only the canonical spelling of each value, and a file only
// when its bytes ARE their own RFC 8785 form: every hash and every signature
// then has exactly one input (cid-governance.md §9, "request files are
// canonical bytes").

import { canonicalize, type CanonicalValue } from '@catalog-canonical-json';
import type { SigningProfile } from './signatures';

/** A JSON object in the form the canonicalizer takes. */
export type JsonObject = { [key: string]: CanonicalValue };

/**
 * Why a federation file or payload is refused. The codes are the ones the C#
 * vector runner reports (`LedgerVectors.CheckRequest`):
 *   • `non_canonical` — the bytes parse but are not their own RFC 8785 form
 *     (pretty-printed, re-encoded, a trailing newline);
 *   • `malformed` — not JSON, or not the shape its type requires;
 *   • `bad_signature` — well formed, but the signature does not verify
 *     against the key the file names.
 */
export type FederationFileErrorCode = 'non_canonical' | 'malformed' | 'bad_signature';

export class FederationFileError extends Error {
    readonly code: FederationFileErrorCode;

    constructor(code: FederationFileErrorCode, message: string) {
        super(message);
        this.name = 'FederationFileError';
        this.code = code;
    }
}

const TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
const SHA512_HEX_RE = /^[0-9a-f]{128}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const BASE64_RE = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

// ── timestamps ──────────────────────────────────────────────────────────

/** `yyyy-MM-ddTHH:mm:ssZ` of `t`, sub-second precision dropped. */
export function formatTimestamp(t: Date): string {
    if (Number.isNaN(t.getTime())) throw new RangeError('formatTimestamp: invalid date');
    return t.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** True when `s` is the canonical spelling of a real instant (no 30 February, no offset, no fraction). */
export function isCanonicalTimestamp(s: unknown): s is string {
    if (typeof s !== 'string' || !TIMESTAMP_RE.test(s)) return false;
    const t = new Date(s);
    return !Number.isNaN(t.getTime()) && t.getUTCFullYear() >= 1 && formatTimestamp(t) === s;
}

// ── identifiers and bytes ───────────────────────────────────────────────

export function isSha512Hex(s: unknown): s is string {
    return typeof s === 'string' && SHA512_HEX_RE.test(s);
}

export function isCanonicalUuid(s: unknown): s is string {
    return typeof s === 'string' && UUID_RE.test(s);
}

export function toBase64(bytes: Uint8Array): string {
    let binary = '';
    for (const b of bytes) binary += String.fromCharCode(b);
    return btoa(binary);
}

/** The bytes of `s` when it is standard padded base64 that re-encodes to
 *  itself (no whitespace, no URL alphabet, no stray padding bits), else null. */
export function decodeCanonicalBase64(s: string): Uint8Array | null {
    if (!BASE64_RE.test(s)) return null;
    let binary: string;
    try {
        binary = atob(s);
    } catch {
        return null;
    }
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return toBase64(bytes) === s ? bytes : null;
}

export function toHex(bytes: Uint8Array): string {
    let hex = '';
    for (const b of bytes) hex += b.toString(16).padStart(2, '0');
    return hex;
}

/** SHA-512 of `bytes`, lowercase hex (Web Crypto). */
export async function sha512Hex(bytes: Uint8Array): Promise<string> {
    return toHex(new Uint8Array(await crypto.subtle.digest('SHA-512', bytes as BufferSource)));
}

/** A map keyed by names from a file (proof and key names). Prototype-free, so
 *  a name such as `__proto__` or `toString` is an ordinary key, never inherited. */
export function dictionary<T>(): Record<string, T> {
    return Object.create(null) as Record<string, T>;
}

export function hasOwn(o: object, name: string): boolean {
    return Object.prototype.hasOwnProperty.call(o, name);
}

export function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}

// ── canonical bytes ─────────────────────────────────────────────────────

const utf8Strict = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true });

/** UTF-8 bytes of a file handed over as text or as bytes. */
export function fileBytes(file: string | Uint8Array): Uint8Array {
    return typeof file === 'string' ? new TextEncoder().encode(file) : file;
}

/**
 * Parses `file` and requires it to be its own RFC 8785 form, byte for byte —
 * the check `FederationJson.ParseCanonical` makes. No transport whitespace is
 * forgiven (a trailing newline is `non_canonical`): a caller that wants to
 * forgive pasted whitespace trims before calling, and everything this library
 * writes is canonical anyway.
 *
 * A number is canonical in its ECMAScript Number::toString form (RFC 8785
 * §3.2.2.3), as `FederationJson.ParseCanonical` reads it; no federation
 * payload carries a fraction today.
 */
export function parseCanonical(file: string | Uint8Array, what: string): unknown {
    const bytes = fileBytes(file);
    let text: string;
    try {
        text = utf8Strict.decode(bytes);
    } catch {
        throw new FederationFileError('malformed', `${what} is not UTF-8.`);
    }

    let value: unknown;
    try {
        value = JSON.parse(text);
    } catch (e) {
        throw new FederationFileError('malformed', `${what} is not JSON: ${(e as Error).message}`);
    }

    let canonical: Uint8Array;
    try {
        canonical = canonicalize(value as CanonicalValue);
    } catch (e) {
        throw new FederationFileError('malformed', `${what} cannot be canonicalized: ${(e as Error).message}`);
    }
    if (!equalBytes(canonical, bytes)) {
        throw new FederationFileError('non_canonical', `${what} is not in canonical (RFC 8785) form.`);
    }
    return value;
}

// ── strict reading ──────────────────────────────────────────────────────

/**
 * Reads one JSON object strictly, as the C# `StrictObject` does: every field
 * typed and in canonical spelling, and {@link end} refuses any field that was
 * not read. A strict reader is what makes rebuilding a payload from its parsed
 * fields safe — a lenient one drops fields it does not model, and the rebuilt
 * bytes no longer match what was signed (cid-governance.md §9).
 */
export class StrictObject {
    private readonly value: Record<string, unknown>;
    private readonly context: string;
    private readonly read = new Set<string>();

    private constructor(value: Record<string, unknown>, context: string) {
        this.value = value;
        this.context = context;
    }

    static of(value: unknown, context: string): StrictObject {
        if (value === null || typeof value !== 'object' || Array.isArray(value)) {
            throw new FederationFileError('malformed', `${context} must be a JSON object.`);
        }
        return new StrictObject(value as Record<string, unknown>, context);
    }

    has(name: string): boolean {
        return hasOwn(this.value, name);
    }

    raw(name: string): unknown {
        if (!this.has(name)) throw this.fail(`'${name}' is required`);
        this.read.add(name);
        return this.value[name];
    }

    string(name: string, maxLength = 512): string {
        const s = this.raw(name);
        if (typeof s !== 'string') throw this.fail(`'${name}' must be a string`);
        if (s.length === 0) throw this.fail(`'${name}' must not be empty`);
        if (s.length > maxLength) throw this.fail(`'${name}' is longer than ${maxLength} characters`);
        if (s.trim() !== s) throw this.fail(`'${name}' has leading or trailing whitespace`);
        return s;
    }

    integer(name: string, min = 0, max = Number.MAX_SAFE_INTEGER): number {
        const n = this.raw(name);
        if (typeof n !== 'number' || !Number.isSafeInteger(n)) throw this.fail(`'${name}' must be an integer`);
        if (n < min || n > max) throw this.fail(`'${name}' must be in [${min}, ${max}]; got ${n}`);
        return n;
    }

    base64(name: string): Uint8Array {
        const bytes = decodeCanonicalBase64(this.string(name, 1 << 20));
        if (bytes === null || bytes.length === 0) throw this.fail(`'${name}' must be non-empty standard base64`);
        return bytes;
    }

    /** An array of strings, in order; what the strings may be is the caller's rule. */
    strings(name: string): string[] {
        const value = this.raw(name);
        if (!Array.isArray(value)) throw this.fail(`'${name}' must be an array`);
        if (!value.every(v => typeof v === 'string')) throw this.fail(`'${name}' must be an array of strings`);
        return [...(value as string[])];
    }

    /** The canonical timestamp string; timestamps stay strings on this side. */
    timestamp(name: string): string {
        const s = this.string(name);
        if (!isCanonicalTimestamp(s)) {
            throw this.fail(`'${name}' must be an RFC 3339 UTC timestamp (yyyy-MM-ddTHH:mm:ssZ); got '${s}'`);
        }
        return s;
    }

    uuid(name: string): string {
        const s = this.string(name);
        if (!isCanonicalUuid(s)) throw this.fail(`'${name}' must be a lowercase hyphenated uuid; got '${s}'`);
        return s;
    }

    profile(name: string): SigningProfile {
        const o = this.object(name);
        const profile: SigningProfile = { algorithm: o.string('algorithm'), parameterSet: o.string('parameter_set') };
        o.end();
        return profile;
    }

    object(name: string): StrictObject {
        const value = this.raw(name);
        if (value === null || typeof value !== 'object' || Array.isArray(value)) {
            throw this.fail(`'${name}' must be an object`);
        }
        return new StrictObject(value as Record<string, unknown>, `${this.context}.${name}`);
    }

    /** The names of every field of this object, in document order. */
    names(): string[] {
        return Object.keys(this.value);
    }

    /** Refuses the object if it carries a field nobody read. */
    end(): void {
        for (const name of Object.keys(this.value)) {
            if (!this.read.has(name)) throw this.fail(`unknown field '${name}'`);
        }
    }

    fail(detail: string): FederationFileError {
        return new FederationFileError('malformed', `${this.context}: ${detail}`);
    }
}
